const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

function dedupeRadioGroups(elements: HTMLElement[]): HTMLElement[] {
    const keepByGroup = new Map<string, HTMLInputElement>();
    elements.forEach((el) => {
        if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return;
        const kept = keepByGroup.get(el.name);
        if (!kept || el.checked) keepByGroup.set(el.name, el);
    });

    const keep = new Set(keepByGroup.values());
    return elements.filter((el) => !(el instanceof HTMLInputElement && el.type === 'radio' && el.name) || keep.has(el));
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
    return dedupeRadioGroups(Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)));
}

function focusBackButton(panel: HTMLElement): void {
    panel.querySelector<HTMLButtonElement>('.flyout-mobile__back-button')?.focus();
}

function getPanelOutsideElements(nav: HTMLElement, flyout: HTMLElement, panel: HTMLElement): HTMLElement[] {
    const elements: HTMLElement[] = [];

    nav.querySelectorAll<HTMLElement>(':scope > .site-header__navigation > :not(.site-header__main-navigation)')
        .forEach((el) => elements.push(el));

    flyout.querySelectorAll<HTMLElement>(':scope > .flyout-mobile__track > *')
        .forEach((el) => {
            if (el !== panel) elements.push(el);
        });

    return elements;
}

function setInert(elements: HTMLElement[], isInert: boolean): void {
    elements.forEach((el) => {
        if (isInert) {
            el.setAttribute('inert', '');
        } else {
            el.removeAttribute('inert');
        }
    });
}

function openFlyoutPanel(nav: HTMLElement, flyout: HTMLElement, trigger: HTMLElement, panel: HTMLElement): void {
    flyout.classList.add('flyout-mobile--panel-open');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    setInert(getPanelOutsideElements(nav, flyout, panel), true);

    focusBackButton(panel);

    const onTransitionEnd = (e: TransitionEvent): void => {
        if (e.target !== panel || e.propertyName !== 'transform') return;
        panel.removeEventListener('transitionend', onTransitionEnd);
        if (panel.classList.contains('is-open') && !panel.contains(document.activeElement)) {
            focusBackButton(panel);
        }

        window.dispatchEvent(new Event('scroll'));
    };
    panel.addEventListener('transitionend', onTransitionEnd);
}

function closeFlyoutPanel(nav: HTMLElement, flyout: HTMLElement, trigger: HTMLElement, panel: HTMLElement): void {
    flyout.classList.remove('flyout-mobile--panel-open');
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    setInert(getPanelOutsideElements(nav, flyout, panel), false);

    // Mirror closeNav: return focus to the control that opened this panel.
    trigger.focus();
}

function resetFlyoutPanels(nav: HTMLElement, flyout: HTMLElement): void {
    flyout.classList.remove('flyout-mobile--panel-open');
    flyout.querySelectorAll<HTMLElement>('.flyout-mobile__level2.is-open').forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        flyout
            .querySelector<HTMLElement>(`.flyout-mobile__level1 [aria-controls="${panel.id}"]`)
            ?.setAttribute('aria-expanded', 'false');
        setInert(getPanelOutsideElements(nav, flyout, panel), false);
    });
}

function findOpenFlyoutPanel(flyout: HTMLElement): { trigger: HTMLElement; panel: HTMLElement } | null {
    const panel = flyout.querySelector<HTMLElement>('.flyout-mobile__level2.is-open');
    if (!panel) return null;

    const trigger = flyout.querySelector<HTMLElement>(`.flyout-mobile__level1 [aria-controls="${panel.id}"]`);
    return trigger ? { trigger, panel } : null;
}

// Wraps Tab/Shift+Tab at the edges of `scope` instead of letting focus escape it.
function trapTab(e: KeyboardEvent, scope: HTMLElement[]): void {
    if (scope.length === 0) return;

    const first = scope[0];
    const last = scope[scope.length - 1];

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

function trapOuterTab(e: KeyboardEvent, nav: HTMLElement, btn: HTMLElement): void {
    const navScope = getFocusableElements(nav);
    if (navScope.length === 0) return;

    const first = navScope[0];
    const last = navScope[navScope.length - 1];
    const active = document.activeElement;

    if (e.shiftKey) {
        if (active === first) {
            e.preventDefault();
            btn.focus();
        } else if (active === btn) {
            e.preventDefault();
            last.focus();
        }
    } else {
        if (active === last) {
            e.preventDefault();
            btn.focus();
        } else if (active === btn) {
            e.preventDefault();
            first.focus();
        }
    }
}

function initFlyoutPanels(nav: HTMLElement, flyout: HTMLElement): void {
    flyout.querySelectorAll<HTMLElement>('.flyout-mobile__level1 [aria-controls]').forEach((trigger) => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        trigger.addEventListener('click', () => openFlyoutPanel(nav, flyout, trigger, panel));

        const backBtn = panel.querySelector<HTMLButtonElement>('.flyout-mobile__back-button');
        backBtn?.addEventListener('click', () => closeFlyoutPanel(nav, flyout, trigger, panel));
    });
}

function getPageOutsideElements(nav: HTMLElement, btn: HTMLElement): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const header = nav.closest<HTMLElement>('header');

    if (header) {
        Array.from(header.children).forEach((child) => {
            if (child !== nav && child !== btn && !child.contains(btn)) {
                elements.push(child as HTMLElement);
            }
        });
    }

    Array.from(document.body.children).forEach((child) => {
        if (child !== header) elements.push(child as HTMLElement);
    });

    return elements;
}

function openNav(btn: HTMLButtonElement, nav: HTMLElement): void {
    btn.setAttribute('aria-expanded', 'true');
    nav.classList.add('is-open');
    document.body.classList.add('mobile-nav-open');
    setInert(getPageOutsideElements(nav, btn), true);

    // The nav is `display: none` until now, so any Ari-responsive images inside it
    // (e.g. the flyout highlight image) couldn't be measured/loaded on page load.
    // Nothing else re-triggers Ari when this drawer opens, so do it explicitly now
    // that it has real dimensions.
    window.dispatchEvent(new Event('scroll'));

    // Move focus into the panel on open. Skip text inputs (the search field is the
    // first focusable element) — focusing one immediately makes iOS Safari zoom the
    // whole page, which looks like the flyout menu itself is zoomed in.
    const focusable = getFocusableElements(nav);
    const firstNonInput = focusable.find((el) => !(el instanceof HTMLInputElement));
    (firstNonInput ?? focusable[0])?.focus();
}

function closeNav(btn: HTMLButtonElement, nav: HTMLElement, flyout: HTMLElement | null): void {
    btn.setAttribute('aria-expanded', 'false');
    nav.classList.remove('is-open');
    document.body.classList.remove('mobile-nav-open');
    setInert(getPageOutsideElements(nav, btn), false);

    // Return focus to the burger button on close
    btn.focus();

    if (flyout) resetFlyoutPanels(nav, flyout);
}

export function initMobileNav(): void {
    const btn = document.querySelector<HTMLButtonElement>('.site-header__burger');
    const nav = document.getElementById('site-mobile-nav');
    if (!btn || !nav) return;

    const flyout = nav.querySelector<HTMLElement>('.flyout-mobile');
    if (flyout) initFlyoutPanels(nav, flyout);

    // Click: toggle open/close
    btn.addEventListener('click', () => {
        btn.getAttribute('aria-expanded') === 'true'
            ? closeNav(btn, nav, flyout)
            : openNav(btn, nav);
    });

    // Escape: close an open flyout panel first, otherwise close the whole dialog
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;

        const openPanel = flyout && findOpenFlyoutPanel(flyout);
        if (openPanel) {
            closeFlyoutPanel(nav, flyout as HTMLElement, openPanel.trigger, openPanel.panel);
            return;
        }

        if (btn.getAttribute('aria-expanded') === 'true') {
            closeNav(btn, nav, flyout);
        }
    });

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        if (btn.getAttribute('aria-expanded') !== 'true') return;

        const openPanel = flyout && findOpenFlyoutPanel(flyout);
        if (openPanel) {
            trapTab(e, getFocusableElements(openPanel.panel));
            return;
        }

        trapOuterTab(e, nav, btn);
    });
}
